/**
 * Analytics consent gate.
 * ---------------------------------------------------------------------------
 * No tracking event is emitted unless ALL of the following hold:
 *   1. the browser does NOT signal Do-Not-Track (`navigator.doNotTrack`,
 *      `window.doNotTrack`, `navigator.msDoNotTrack`)
 *   2. the browser does NOT signal Global Privacy Control (`navigator.globalPrivacyControl`)
 *   3. the visitor has explicitly granted consent (stored in localStorage)
 *
 * Consent is opt-in: while the decision is "unset", nothing is tracked.
 */

export const CONSENT_KEY = "sfn.analytics.consent";
export type ConsentState = "granted" | "denied" | "unset";

type ConsentListener = (state: ConsentState) => void;
const listeners = new Set<ConsentListener>();

/** True when the browser signals Do-Not-Track or Global Privacy Control. */
export function isPrivacySignalSet(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & {
    msDoNotTrack?: string;
    globalPrivacyControl?: boolean;
  };
  const win = window as Window & { doNotTrack?: string };
  const dnt = nav.doNotTrack ?? win.doNotTrack ?? nav.msDoNotTrack;
  if (dnt === "1" || dnt === "yes") return true;
  if (nav.globalPrivacyControl === true) return true;
  return false;
}

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return "unset";
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw === "granted" || raw === "denied" ? raw : "unset";
  } catch {
    return "unset";
  }
}

export function setConsent(state: Exclude<ConsentState, "unset">): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, state);
  } catch {
    /* storage blocked — consent stays session-local (denied by default) */
  }
  listeners.forEach((fn) => fn(state));
}

/** Single decision point used by the tracker. */
export function canTrack(): boolean {
  if (typeof window === "undefined") return false;
  if (isPrivacySignalSet()) return false;
  return getConsent() === "granted";
}

/** True when we should still ask the visitor (no signal, no stored decision). */
export function shouldPromptForConsent(): boolean {
  if (typeof window === "undefined") return false;
  if (isPrivacySignalSet()) return false;
  return getConsent() === "unset";
}

export function subscribeConsent(fn: ConsentListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
